import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError, downloadFile } from "@/lib/api";
import TeamSelector from "@/components/TeamSelector";
import { toast } from "sonner";
import { Shield, Send, Copy, Trash2, Download, Users, Shirt, Megaphone, Archive, ArchiveRestore, Search, Plus, UserPlus, X } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const EMPTY_NEW_PLAYER = { first_name: "", last_name: "", graduation_year: "", primary_position: "", secondary_position: "", school: "", province: "", bats: "", throws: "", date_of_birth: "", current_team_id: "" };

const STATUSES = ["pending", "invited", "accepted", "declined", "alternate", "confirmed"];
const UNIFORM_STATUSES = ["not_ordered", "ordered", "received", "distributed"];
const AUDIENCES = ["all", "parents", "players", "coaches", "staff"];

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-slate-100 text-slate-600 border border-slate-300" },
  invited: { label: "Invited", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800 border border-emerald-300" },
  declined: { label: "Declined", cls: "bg-red-100 text-red-700 border border-red-300" },
  alternate: { label: "Alternate", cls: "bg-blue-100 text-blue-800 border border-blue-300" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-600 text-white" },
};

const TABS = [
  { key: "roster", label: "Roster", icon: Users },
  { key: "uniforms", label: "Uniforms", icon: Shirt },
  { key: "comms", label: "Communications", icon: Megaphone },
];

export default function AdminTeamRoster() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [camps, setCamps] = useState([]);
  const [imp, setImp] = useState({ camp_id: "", selection_status: "invited", roster_status: "pending" });
  const [tab, setTab] = useState("roster");

  const load = useCallback(() => {
    api.get(`/admin/teams/${teamId}/roster`).then(({ data }) => setData(data)).catch((e) => {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      setData({ team: null, roster: [], summary: {} });
    });
  }, [teamId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/camps").then(({ data }) => setCamps(data.items || [])).catch(() => {}); }, []);

  const doImport = async () => {
    if (!imp.camp_id) { toast.error("Choose a camp to import from"); return; }
    try {
      const { data } = await api.post(`/admin/teams/${teamId}/import-from-camp`, imp);
      toast.success(data.added ? `Imported ${data.added} player(s)` : "No matching selections on that camp");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const sendInvite = async (e) => {
    try {
      const { data } = await api.post(`/admin/teams/${teamId}/roster/${e.player_id}/send-invite`);
      toast.success(`Invite sent · ${data.emails_queued} email(s) queued (mocked)`);
      copyLink(data.invitation_link);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  const copyLink = (link) => {
    if (!link) return;
    const full = link.startsWith("http") ? link : `${window.location.origin}${link}`;
    navigator.clipboard?.writeText(full).then(() => toast.success("Invitation link copied"), () => {});
  };

  const patchEntry = async (e, body, successMsg) => {
    try {
      await api.patch(`/admin/teams/${teamId}/roster/${e.player_id}`, body);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  const remove = async (e) => {
    if (!window.confirm("Remove this player from the roster?")) return;
    try {
      await api.delete(`/admin/teams/${teamId}/roster/${e.player_id}`);
      toast.success("Removed from roster");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  const addPlayer = async (playerId, status = "confirmed") => {
    try {
      await api.post(`/admin/teams/${teamId}/roster`, { player_id: playerId, status });
      toast.success("Player added to roster");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  const deleteTeam = async () => {
    if (!window.confirm(`Delete team "${data?.team?.name || "this team"}"? This removes the team and its roster entries. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/teams/${teamId}`);
      toast.success("Team deleted");
      navigate("/admin/teams");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  const setArchived = async (archived) => {
    try {
      await api.patch(`/admin/teams/${teamId}`, { status: archived ? "archived" : "active" });
      toast.success(archived ? "Team archived" : "Team restored");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
  };

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading roster…</div></AdminLayout>;
  const team = data.team || {};
  const s = data.summary || {};

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <Link to="/admin/teams" className="label-overline text-slate-500 hover:text-[#E63946]">← All Teams</Link>
        <div className="flex items-center gap-2 label-overline text-[#E63946] mt-3"><Shield className="w-4 h-4" /> CBR Nation</div>
        <h1 data-testid="roster-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{team.name || "Team"}
          {team.status === "archived" && <span className="ml-3 align-middle text-[10px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-600 px-2 py-1 rounded-sm">Archived</span>}
        </h1>
        <p className="text-xs text-slate-500 mt-1">{[team.season, team.division].filter(Boolean).join(" · ") || "Roster management"}</p>
        <button
          data-testid="export-roster-csv"
          onClick={() => downloadFile(`/admin/teams/${teamId}/roster.csv`, {}, `cbr-roster.csv`)
            .then(() => toast.success("Roster CSV exported"))
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Export failed"))}
          className="mt-3 inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50"
        >
          <Download className="w-4 h-4" /> Export Roster CSV
        </button>
        {team.status === "archived" ? (
          <button
            data-testid="restore-team-btn"
            onClick={() => setArchived(false)}
            className="mt-3 ml-2 inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-emerald-600 hover:text-white transition-colors"
          >
            <ArchiveRestore className="w-4 h-4" /> Restore Team
          </button>
        ) : (
          <button
            data-testid="archive-team-btn"
            onClick={() => setArchived(true)}
            className="mt-3 ml-2 inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#0A192F] hover:text-white transition-colors"
          >
            <Archive className="w-4 h-4" /> Archive Team
          </button>
        )}
        <button
          data-testid="delete-team-btn"
          onClick={deleteTeam}
          className="mt-3 ml-2 inline-flex items-center gap-2 border border-[#E63946] text-[#E63946] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#E63946] hover:text-white transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Delete Team
        </button>

        {/* Summary tiles */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mt-6">
          <Tile testid="sum-total" label="Total" val={s.total || 0} cls="text-[#0A192F]" />
          {STATUSES.map((st) => (
            <Tile key={st} testid={`sum-${st}`} label={STATUS_META[st].label} val={s[st] || 0} cls="text-slate-700" />
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`team-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 ${tab === t.key ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "roster" && (
          <RosterTab data={data} camps={camps} imp={imp} setImp={setImp} doImport={doImport}
            sendInvite={sendInvite} copyLink={copyLink} patchEntry={patchEntry} remove={remove} addPlayer={addPlayer} />
        )}
        {tab === "uniforms" && <UniformsTab data={data} patchEntry={patchEntry} />}
        {tab === "comms" && <CommsTab teamId={teamId} />}
      </div>
    </AdminLayout>
  );
}

function AddPlayerBox({ addPlayer, existingIds }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW_PLAYER);
  const [creating, setCreating] = useState(false);
  const [invitePrompt, setInvitePrompt] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/players`, { params: { q: q.trim(), limit: 8 } });
        setResults(data.items || []);
        setSearched(true);
        setOpen(true);
      } catch (_) { setResults([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const reset = () => { setQ(""); setResults([]); setOpen(false); setSearched(false); };

  const pick = async (p) => {
    await addPlayer(p.id, "confirmed");
    reset();
  };

  const openCreate = () => {
    const parts = q.trim().split(/\s+/);
    setForm({ ...EMPTY_NEW_PLAYER, first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "" });
    setShowCreate(true);
    setOpen(false);
  };

  const createAndAdd = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name are required"); return; }
    setCreating(true);
    try {
      const raw = { ...form, graduation_year: form.graduation_year ? Number(form.graduation_year) : null };
      const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== "" && v !== null));
      const { data } = await api.post(`/admin/players`, payload);
      await addPlayer(data.id, "confirmed");
      toast.success(`${data.first_name} ${data.last_name} created & added to roster`);
      setForm(EMPTY_NEW_PLAYER); setShowCreate(false); reset();
      setInviteEmail("");
      setInvitePrompt({ id: data.id, name: `${data.first_name} ${data.last_name}`.trim() });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  const sendInviteNow = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter the player's email to send the invite"); return; }
    setInviting(true);
    try {
      const { data } = await api.post(`/admin/players/${invitePrompt.id}/invite`, { email: inviteEmail.trim() });
      const link = data.claim_link ? (data.claim_link.startsWith("http") ? data.claim_link : `${window.location.origin}${data.claim_link}`) : null;
      if (link && navigator.clipboard) { try { await navigator.clipboard.writeText(link); toast.success("Invite sent — claim link copied"); } catch (_) { toast.success("Claim invite sent"); } }
      else { toast.success("Claim invite sent"); }
      setInvitePrompt(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setInviting(false); }
  };

  const metaLine = (p) => [
    p.graduation_year ? `Class of ${p.graduation_year}` : null,
    [p.primary_position, p.secondary_position].filter(Boolean).join(" / "),
    p.school,
    p.province,
  ].filter(Boolean).join(" · ") || "No details on file";

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="add-player-box">
      <div className="flex items-center gap-2 label-overline text-[10px] mb-3"><UserPlus className="w-4 h-4 text-[#E63946]" /> Add Player Directly</div>
      <div className="relative">
        <div className="flex items-center gap-2 border border-slate-300 rounded-sm px-3">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            data-testid="roster-add-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => (results.length || searched) && setOpen(true)}
            placeholder="Search players by name or school…"
            className="w-full py-2.5 text-sm outline-none"
          />
          {loading && <span className="text-[10px] text-slate-400">…</span>}
        </div>
        {open && (results.length > 0 || (searched && !loading)) && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-80 overflow-y-auto" data-testid="roster-add-results">
            {results.map((p) => {
              const already = existingIds.has(p.id);
              return (
                <button
                  key={p.id}
                  data-testid={`roster-add-result-${p.id}`}
                  disabled={already}
                  onClick={() => pick(p)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50 disabled:opacity-50 border-b border-slate-100 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{metaLine(p)}</div>
                  </div>
                  {already ? <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">On roster</span> : <Plus className="w-4 h-4 text-[#E63946] shrink-0" />}
                </button>
              );
            })}
            {results.length === 0 && searched && !loading && (
              <div className="px-3 py-4 text-center" data-testid="roster-add-no-results">
                <p className="text-sm text-slate-600">No matching players found.</p>
                <button
                  data-testid="roster-add-create-new"
                  onClick={openCreate}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> Create New Player
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Added players are set to <span className="font-semibold">Confirmed</span> — change status in the roster below, or use “Import from Camp” for bulk selection.</p>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="create-player-modal">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-display text-xl uppercase tracking-tight">Create New Player</h3>
              <button data-testid="create-player-close" onClick={() => setShowCreate(false)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline text-[10px]">First Name *</label>
                <input data-testid="create-player-first_name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline text-[10px]">Last Name *</label>
                <input data-testid="create-player-last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline text-[10px]">Grad Year</label>
                <input data-testid="create-player-graduation_year" type="number" value={form.graduation_year} onChange={(e) => setForm({ ...form, graduation_year: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline text-[10px]">School</label>
                <input data-testid="create-player-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline text-[10px]">Primary Position</label>
                <select data-testid="create-player-primary_position" value={form.primary_position} onChange={(e) => setForm({ ...form, primary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="">—</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Secondary Position</label>
                <select data-testid="create-player-secondary_position" value={form.secondary_position} onChange={(e) => setForm({ ...form, secondary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="">— None —</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Province</label>
                <select data-testid="create-player-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="">—</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Bats</label>
                <select data-testid="create-player-bats" value={form.bats} onChange={(e) => setForm({ ...form, bats: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="">—</option>
                  <option value="L">L</option>
                  <option value="R">R</option>
                  <option value="S">S</option>
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Throws</label>
                <select data-testid="create-player-throws" value={form.throws} onChange={(e) => setForm({ ...form, throws: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="">—</option>
                  <option value="L">L</option>
                  <option value="R">R</option>
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Date of Birth <span className="normal-case text-slate-400">(private, optional)</span></label>
                <input data-testid="create-player-date_of_birth" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            </div>
            <div className="px-5 pb-4">
              <label className="label-overline text-[10px]">Current Team <span className="normal-case text-slate-400">(optional — completable later)</span></label>
              <div className="mt-1"><TeamSelector value={form.current_team_id} onChange={(t) => setForm({ ...form, current_team_id: t?.id || "" })} testidPrefix="roster-np-team" /></div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button data-testid="create-player-save" onClick={createAndAdd} disabled={creating} className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
                {creating ? "Creating…" : "Create & Add to Roster"}
              </button>
            </div>
          </div>
        </div>
      )}

      {invitePrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="invite-prompt-modal">
          <div className="bg-white w-full max-w-md rounded-sm p-6">
            <h3 className="font-display text-2xl uppercase tracking-tighter">Player Created</h3>
            <p className="text-sm text-slate-600 mt-2">
              <span className="font-semibold">{invitePrompt.name}</span> was added to the roster and the national player database. Would you like to invite them to claim their profile?
            </p>
            <div className="mt-4">
              <label className="label-overline text-[10px]">Player / Parent Email</label>
              <input
                data-testid="invite-prompt-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="player@example.com"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="invite-later-btn" onClick={() => setInvitePrompt(null)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Invite Later</button>
              <button data-testid="invite-now-btn" onClick={sendInviteNow} disabled={inviting} className="px-5 py-2 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
                {inviting ? "Sending…" : "Invite Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RosterTab({ data, camps, imp, setImp, doImport, sendInvite, copyLink, patchEntry, remove, addPlayer }) {
  const existingIds = new Set((data.roster || []).map((e) => e.player_id));
  return (
    <>
      <AddPlayerBox addPlayer={addPlayer} existingIds={existingIds} />

      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 label-overline text-[10px] mb-3"><Download className="w-4 h-4 text-[#E63946]" /> Import from Camp Selection (Top 40)</div>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="label-overline text-[10px]">Camp</span>
            <select data-testid="import-camp-select" value={imp.camp_id} onChange={(ev) => setImp({ ...imp, camp_id: ev.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
              <option value="">Select a camp…</option>
              {camps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">From Selection</span>
            <select data-testid="import-selection-status" value={imp.selection_status} onChange={(ev) => setImp({ ...imp, selection_status: ev.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
              <option value="invited">Invited</option>
              <option value="alternate">Alternate</option>
            </select>
          </label>
          <button data-testid="import-submit" onClick={doImport} className="bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">Import</button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left p-3 label-overline">Player</th>
              <th className="text-left p-3 label-overline">Pos</th>
              <th className="text-left p-3 label-overline">Class</th>
              <th className="text-left p-3 label-overline">CBR</th>
              <th className="text-left p-3 label-overline">#</th>
              <th className="text-left p-3 label-overline">Status</th>
              <th className="text-right p-3 label-overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.roster.map((e) => {
              const p = e.player || {};
              const meta = STATUS_META[e.status] || STATUS_META.pending;
              return (
                <tr key={e.player_id} data-testid={`roster-row-${e.player_id}`} className="border-b border-slate-100 last:border-0">
                  <td className="p-3">
                    <Link to={`/prospects/${p.slug}`} className="font-semibold hover:text-[#E63946]">{p.first_name} {p.last_name}</Link>
                    <div className="text-[11px] text-slate-500">
                      {p.province || "—"}
                      {p.user_id === null && <span data-testid={`unclaimed-${e.player_id}`} className="ml-2 text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">Unclaimed</span>}
                    </div>
                  </td>
                  <td className="p-3 font-data">{p.primary_position || "—"}</td>
                  <td className="p-3 font-data">{p.graduation_year || "—"}</td>
                  <td className="p-3 font-data text-xl text-[#0A192F]">{p.cbr_overall_rating != null ? Number(p.cbr_overall_rating).toFixed(1) : "—"}</td>
                  <td className="p-3">
                    <input data-testid={`jersey-input-${e.player_id}`} type="number" defaultValue={e.jersey_number ?? ""}
                      onBlur={(ev) => ev.target.value !== "" && patchEntry(e, { jersey_number: Number(ev.target.value) }, "Jersey number saved")}
                      className="w-16 border border-slate-300 rounded-sm px-2 py-1 text-sm" placeholder="#" />
                  </td>
                  <td className="p-3">
                    <select data-testid={`status-select-${e.player_id}`} value={e.status} onChange={(ev) => patchEntry(e, { status: ev.target.value })}
                      className={`px-2 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-sm ${meta.cls}`}>
                      {STATUSES.map((st) => <option key={st} value={st}>{STATUS_META[st].label}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconBtn testid={`send-invite-${e.player_id}`} onClick={() => sendInvite(e)} title="Send invite"><Send className="w-3.5 h-3.5" /></IconBtn>
                      {e.invitation_link && <IconBtn testid={`copy-link-${e.player_id}`} onClick={() => copyLink(e.invitation_link)} title="Copy invitation link"><Copy className="w-3.5 h-3.5" /></IconBtn>}
                      <IconBtn testid={`remove-${e.player_id}`} onClick={() => remove(e)} title="Remove" danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.roster.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-sm text-slate-500"><Users className="w-6 h-6 mx-auto mb-2 text-slate-300" />No players on this roster yet. Import from a camp selection board above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UniformsTab({ data, patchEntry }) {
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left p-3 label-overline">Player</th>
            <th className="text-left p-3 label-overline">Jersey #</th>
            <th className="text-left p-3 label-overline">Jersey</th>
            <th className="text-left p-3 label-overline">Hat</th>
            <th className="text-left p-3 label-overline">Shirt</th>
            <th className="text-left p-3 label-overline">Pant</th>
            <th className="text-left p-3 label-overline">Uniform Status</th>
          </tr>
        </thead>
        <tbody>
          {data.roster.map((e) => {
            const p = e.player || {};
            return (
              <tr key={e.player_id} data-testid={`uniform-row-${e.player_id}`} className="border-b border-slate-100 last:border-0">
                <td className="p-3 font-semibold">{p.first_name} {p.last_name}</td>
                <SizeCell e={e} field="jersey_number" type="number" patchEntry={patchEntry} />
                <SizeCell e={e} field="jersey_size" patchEntry={patchEntry} />
                <SizeCell e={e} field="hat_size" patchEntry={patchEntry} />
                <SizeCell e={e} field="shirt_size" patchEntry={patchEntry} />
                <SizeCell e={e} field="pant_size" patchEntry={patchEntry} />
                <td className="p-3">
                  <select data-testid={`uniform-status-${e.player_id}`} value={e.uniform_status || "not_ordered"}
                    onChange={(ev) => patchEntry(e, { uniform_status: ev.target.value }, "Uniform status saved")}
                    className="border border-slate-300 rounded-sm px-2 py-1 text-[11px] uppercase tracking-wider">
                    {UNIFORM_STATUSES.map((u) => <option key={u} value={u}>{u.replace("_", " ")}</option>)}
                  </select>
                </td>
              </tr>
            );
          })}
          {data.roster.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-sm text-slate-500">No players on this roster yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SizeCell({ e, field, type = "text", patchEntry }) {
  return (
    <td className="p-3">
      <input data-testid={`${field}-${e.player_id}`} type={type} defaultValue={e[field] ?? ""}
        onBlur={(ev) => {
          const raw = ev.target.value.trim();
          if (raw === (e[field] ?? "")) return;
          const val = type === "number" ? (raw === "" ? null : Number(raw)) : (raw === "" ? null : raw);
          if (val !== null) patchEntry(e, { [field]: val }, "Saved");
        }}
        className="w-16 border border-slate-300 rounded-sm px-2 py-1 text-sm" placeholder="—" />
    </td>
  );
}

function CommsTab({ teamId }) {
  const [form, setForm] = useState({ subject: "", body: "", audience: "all" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(() => {
    api.get(`/admin/teams/${teamId}/communications`).then(({ data }) => setHistory(data.items || [])).catch(() => {});
  }, [teamId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/teams/${teamId}/communications`, form);
      toast.success(`Announcement sent · ${data.recipient_count} recipient(s) queued (mocked)`);
      setForm({ subject: "", body: "", audience: "all" });
      loadHistory();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display text-lg uppercase tracking-tight">New Announcement</h3>
        <p className="text-[11px] text-slate-500 mt-1">Email is mocked (logged to DB) until a provider is connected.</p>
        <label className="flex flex-col gap-1 mt-4">
          <span className="label-overline text-[10px]">Audience</span>
          <select data-testid="comm-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
            {AUDIENCES.map((a) => <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 mt-3">
          <span className="label-overline text-[10px]">Subject</span>
          <input data-testid="comm-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" placeholder="Travel update for this weekend" />
        </label>
        <label className="flex flex-col gap-1 mt-3">
          <span className="label-overline text-[10px]">Message</span>
          <textarea data-testid="comm-body" rows="5" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" placeholder="Write your announcement…" />
        </label>
        <button data-testid="comm-send" disabled={busy} onClick={send} className="mt-4 bg-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
          {busy ? "Sending…" : "Send Announcement"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display text-lg uppercase tracking-tight">History</h3>
        <div className="mt-4 space-y-3" data-testid="comm-history">
          {history.length === 0 && <p className="text-sm text-slate-500">No announcements sent yet.</p>}
          {history.map((c) => (
            <div key={c.id} data-testid={`comm-item-${c.id}`} className="border border-slate-100 rounded-sm p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{c.subject}</span>
                <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-sm">{c.audience}</span>
              </div>
              <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{c.body}</p>
              <div className="text-[10px] text-slate-400 mt-2">{c.recipient_count} recipient(s) · {new Date(c.created_at).toLocaleString("en-CA")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, val, cls, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-3 rounded-sm">
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-data text-2xl mt-0.5 ${cls}`}>{val}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`p-2 rounded-sm border ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}
